import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import authService from '../services/authService';
import { UserProfile } from '../services/userService';
import ranksService, { getRankProgress, getTierColor, getRankLore, getRankImagePath } from '../services/ranksService';
import { Link } from 'react-router-dom';
import { Gamepad, Target, Flame, CheckCircle } from 'lucide-react';
import './ProfilePage.css';

const ProfilePage: React.FC = () => {
  const { user, isAuthenticated } = useAuth0();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        if (!isAuthenticated || !user) {
          setError('Please log in to view your profile');
          setLoading(false);
          return;
        }

        const profile = await authService.getProfile();
        setUserProfile(profile);
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch profile:', err);
        setError('Failed to load profile data');
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [isAuthenticated, user]);

  if (loading) {
    return (
      <div className="profile-page">
        <div className="profile-loading">
          <div className="loading-spinner"></div>
          <p>Loading your profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="profile-page">
        <div className="profile-error">
          <h2>Oops! Something went wrong</h2>
          <p>{error}</p>
          <Link to="/" className="back-home-btn">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div className="profile-page">
        <div className="profile-error">
          <h2>Profile not found</h2>
          <p>Unable to load your profile data.</p>
          <Link to="/" className="back-home-btn">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  // Debug logging
  console.log('ProfilePage: userProfile received:', userProfile);
  console.log('ProfilePage: totalPoints value:', userProfile.totalPoints);
  console.log('ProfilePage: totalPoints type:', typeof userProfile.totalPoints);

  const rankProgress = getRankProgress(userProfile.totalPoints);
  const tierColor = getTierColor(rankProgress.currentRank.tier);
  const rankLore = getRankLore(rankProgress.currentRank.name);

  // Calculate stats
  const accuracy = userProfile.totalAnswers > 0 
    ? Math.round((userProfile.correctAnswers / userProfile.totalAnswers) * 100)
    : 0;

  return (
    <div className="profile-page">
      <div className="profile-container">
        {/* Profile Header */}
        <div className="profile-header">
          <div className="profile-avatar">
            <img 
              src={getRankImagePath(rankProgress.currentRank.name)} 
              alt={`${rankProgress.currentRank.name} rank`}
              className="profile-rank-image"
            />
          </div>
          
          <div className="profile-info">
            <h1 className="profile-name">
              {userProfile.displayName || userProfile.name}
            </h1>
            <div className="profile-rank-info">
              <span className="profile-rank-name" style={{ color: tierColor }}>
                {rankProgress.currentRank.name}
              </span>
              <span className="profile-tier">
                {rankProgress.currentRank.tier.toUpperCase()} TIER
              </span>
            </div>
            <div className="profile-points">
              <span className="points-value">{(userProfile.totalPoints || 0).toLocaleString()}</span>
              <span className="points-label">Total Points</span>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="profile-stats-grid">
          <div className="profile-stat-card">
            <div className="stat-icon">
              <Gamepad size={24} color="#3b82f6" />
            </div>
            <div className="stat-value">{userProfile.gamesPlayed || 0}</div>
            <div className="stat-label">Games Played</div>
          </div>

          <div className="profile-stat-card">
            <div className="stat-icon">
              <Target size={24} color="#3b82f6" />
            </div>
            <div className="stat-value">{accuracy}%</div>
            <div className="stat-label">Accuracy</div>
          </div>

          <div className="profile-stat-card">
            <div className="stat-icon">
              <Flame size={24} color="#3b82f6" />
            </div>
            <div className="stat-value">{userProfile.bestStreak || 0}</div>
            <div className="stat-label">Best Streak</div>
          </div>

          <div className="profile-stat-card">
            <div className="stat-icon">
              <CheckCircle size={24} color="#3b82f6" />
            </div>
            <div className="stat-value">{userProfile.correctAnswers || 0}</div>
            <div className="stat-label">Correct Answers</div>
          </div>
        </div>

        {/* Rank Description */}
        <div className="profile-rank-description">
          <h2>Your Biblical Rank</h2>
          <div className="rank-description-card">
            <div className="rank-description-header">
              <div className="rank-description-icon">
                <img
                  src={getRankImagePath(rankProgress.currentRank.name)}
                  alt={`${rankProgress.currentRank.name} rank`}
                  className="rank-description-image"
                />
              </div>
              <div className="rank-description-info">
                <h3>{rankProgress.currentRank.name}</h3>
                <p className="rank-description-tier" style={{ color: tierColor }}>
                  {rankProgress.currentRank.tier.toUpperCase()} TIER
                </p>
              </div>
            </div>
            
            <blockquote className="rank-description-quote" style={{ borderLeftColor: tierColor }}>
              "{rankLore.openingQuote}"
            </blockquote>
            
            <div className="rank-description-content">
              {rankLore.mainDescription.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
            
            <div className="rank-description-example">
              <h4 style={{ color: tierColor }}>Biblical Example:</h4>
              <p><strong>{rankLore.biblicalExampleTitle}</strong></p>
              <p className="example-description">{rankLore.biblicalExampleDescription}</p>
            </div>
          </div>
        </div>

        {/* Progress to Next Rank */}
        <div className="profile-progress">
          <h2>Your Progress</h2>
          <div className="progress-card">
            <div className="progress-info">
              <div className="current-rank">
                <img
                  src={getRankImagePath(rankProgress.currentRank.name)}
                  alt={`${rankProgress.currentRank.name} rank`}
                  className="progress-rank-image"
                />
                <span className="progress-rank-name">{rankProgress.currentRank.name}</span>
              </div>
              <div className="progress-arrow">→</div>
              <div className="next-rank">
                {rankProgress.nextRank ? (
                  <>
                    <img
                      src={getRankImagePath(rankProgress.nextRank.name)}
                      alt={`${rankProgress.nextRank.name} rank`}
                      className="progress-rank-image"
                    />
                    <span className="progress-rank-name">{rankProgress.nextRank.name}</span>
                  </>
                ) : (
                  <span className="progress-rank-name">MAX RANK</span>
                )}
              </div>
            </div>
            
            {!rankProgress.isMaxRank && (
              <div className="progress-details">
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ 
                      width: `${rankProgress.progressPercentage}%`,
                      backgroundColor: tierColor
                    }}
                  />
                </div>
                <div className="progress-text">
                  <span>{rankProgress.progressText}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="profile-actions">
          <Link to="/play" className="profile-action-btn primary">
            Play Now
          </Link>
          <Link to="/ranks" className="profile-action-btn secondary">
            View All Ranks
          </Link>
          <Link to="/leaderboard" className="profile-action-btn secondary">
            Leaderboard
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;